import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FutureSubscriptionsQuery,
  PricingModelVersion,
  SubscriptionLimitDataQuery,
  SubscriptionPlanType,
  useEnableVenueAutoCategorizationMutation,
  useFutureSubscriptionsLazyQuery,
  useSubscriptionLimitDataLazyQuery,
  useUpdateVenueInvoiceLimitMutation,
  useUpdateVenueRenewalDayMutation,
  useVenuesForSubscriptionLimit,
} from '../../generated/graphql';
import { useAuth } from '../../providers/auth.provider';
import { BillingLinkModal } from './BillingLinkModal';

type VenueOption = {
  id: string;
  name: string;
  isTest: boolean;
  useAutoCategorization: boolean;
};

export const InvoiceLimitManager = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [newLimit, setNewLimit] = useState('');
  const [newCostRadarLimit, setNewCostRadarLimit] = useState('');
  const [planType, setPlanType] = useState<SubscriptionPlanType | ''>('');
  const [fromNextCycle, setFromNextCycle] = useState<'false' | 'true'>('false');
  const [oneTime, setOneTime] = useState<'false' | 'true'>('true');
  const [newRenewalDay, setNewRenewalDay] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingThenUpdate, setBillingThenUpdate] = useState(false);
  const [enableAutoCategorization, setEnableAutoCategorization] =
    useState(false);
  const [autoCategorizationWarningOpen, setAutoCategorizationWarningOpen] =
    useState(false);

  /* derived: invalid renewal-day? */
  const renewalDayError =
    newRenewalDay !== '' &&
    (Number(newRenewalDay) < 1 || Number(newRenewalDay) > 31);

  const [loadData, { data: subscriptionData }] =
    useSubscriptionLimitDataLazyQuery();
  const { data: venuesData, loading: venuesLoading } =
    useVenuesForSubscriptionLimit();
  const [updateLimit] = useUpdateVenueInvoiceLimitMutation();
  const [updateRenewalDay] = useUpdateVenueRenewalDayMutation();
  const [enableVenueAutoCategorization] =
    useEnableVenueAutoCategorizationMutation();
  const [loadFuture, { data: futureData }] = useFutureSubscriptionsLazyQuery();

  const [subscriptionDataState, setSubscriptionDataState] =
    useState<SubscriptionLimitDataQuery | null>(null);

  const [futureSubs, setFutureSubs] = useState<
    NonNullable<FutureSubscriptionsQuery['futureSubscriptions']>
  >([]);
  const [force, setForce] = useState(false);

  // Disable plan selection when renewal-day is provided
  const planTypeDisabled = newRenewalDay !== '';
  const planTypeTooltip = planTypeDisabled
    ? 'Cannot change plan when renewal day is modified.'
    : '';

  // Disable renewal day when a plan change is selected or future rows exist
  const renewalDayDisabled = planType !== '' || futureSubs.length > 0;
  let renewalDayTooltip = '';
  if (planType !== '') {
    renewalDayTooltip = 'Cannot change renewal day when changing plan.';
  } else if (futureSubs.length > 0) {
    renewalDayTooltip =
      'Renewal day cannot be changed while future subscriptions exist.';
  }

  useEffect(() => {
    if (subscriptionData) {
      setSubscriptionDataState(subscriptionData);
    }
  }, [subscriptionData]);

  useEffect(() => {
    if (futureData?.futureSubscriptions) {
      setFutureSubs(futureData.futureSubscriptions);
    } else {
      setFutureSubs([]);
    }
  }, [futureData]);

  const venues = useMemo<VenueOption[]>(() => {
    if (!venuesData?.venues) return [];
    const unique = new Map<string, VenueOption>();
    venuesData.venues.forEach((v) =>
      unique.set(v.id, {
        id: v.id,
        name: v.name,
        isTest: !!v.isTest,
        useAutoCategorization: !!v.useAutoCategorization,
      }),
    );
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;
  const venueAlreadyAutoCategorized =
    selectedVenue?.useAutoCategorization ?? false;
  const { user } = useAuth();
  const canMigrateAutoCategorization =
    !!user?.adminPermissions.canMigrateAutoCategorization;
  const autoCategorizationDisabled =
    venueAlreadyAutoCategorized || !canMigrateAutoCategorization;
  let autoCategorizationDisabledReason = '';
  if (venueAlreadyAutoCategorized) {
    autoCategorizationDisabledReason =
      'Autocategorization is already enabled for this venue and cannot be turned off.';
  } else if (!canMigrateAutoCategorization) {
    autoCategorizationDisabledReason =
      'You do not have permission to migrate venues to autocategorization.';
  }
  const autoCategorizationNewlyEnabled =
    enableAutoCategorization && !venueAlreadyAutoCategorized;

  const renewalDay = useMemo(() => {
    const currentSubscriptionDate =
      subscriptionDataState?.subscriptionData?.end ||
      subscriptionDataState?.subscriptionData?.start;
    if (currentSubscriptionDate == null) return null;
    return DateTime.fromMillis(currentSubscriptionDate).day;
  }, [subscriptionDataState]);

  const isV1 =
    subscriptionDataState?.subscriptionData?.pricingModelVersion ===
    PricingModelVersion.V1;

  const handleVenueChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: VenueOption | null,
  ) => {
    setSelectedVenueId(value?.id ?? '');
    setEnableAutoCategorization(value?.useAutoCategorization ?? false);
    // Clear the previous venue's data immediately so the paywall gate and the details
    // panel are never evaluated against stale data while the new venue loads.
    setSubscriptionDataState(null);
    setFutureSubs([]);
    if (value) {
      loadData({ variables: { venueId: value.id } });
      loadFuture({ variables: { venueId: value.id } });
    }
  };

  const openConfirmDialog = () => {
    if (
      !selectedVenueId ||
      (!newLimit &&
        !newCostRadarLimit &&
        !newRenewalDay &&
        !autoCategorizationNewlyEnabled) ||
      (isV1 && !planType && (newLimit !== '' || newCostRadarLimit !== '')) ||
      renewalDayError ||
      (planType !== '' && newRenewalDay !== '')
    )
      return;
    setConfirmOpen(true);
  };

  const resetForm = () => {
    setConfirmOpen(false);
    setAutoCategorizationWarningOpen(false);
    setSelectedVenueId('');
    setNewLimit('');
    setNewCostRadarLimit('');
    setNewRenewalDay('');
    setPlanType('');
    setFromNextCycle('false');
    setOneTime('true');
    setSubscriptionDataState(null);
    setFutureSubs([]);
    setForce(false);
    setEnableAutoCategorization(false);
  };

  const runSubscriptionUpdate = async () => {
    // The mutation wrapper returns null on failure (it toasts, never throws). Bail
    // without resetting so a failed update keeps the form populated for retry - critical
    // on the leaving-paywall path where the billing link is already committed.
    if (newLimit || newCostRadarLimit) {
      const res = await updateLimit({
        variables: {
          venueId: selectedVenueId,
          ...(newLimit && { invoiceLimit: Number(newLimit) }),
          ...(newCostRadarLimit && {
            costRadarLimit: Number(newCostRadarLimit),
          }),
          planType: planType || undefined,
          fromNextCycle: fromNextCycle === 'true',
          oneTime: oneTime === 'true',
          force,
        },
        toastConfig: {
          successText: 'Subscription limits updated successfully',
          errorText: 'Failed to update subscription limits. Please try again.',
        },
      });
      if (!res?.data) return;
    }

    if (newRenewalDay) {
      const res = await updateRenewalDay({
        variables: {
          venueId: selectedVenueId,
          renewalDay: Number(newRenewalDay),
          fromNextCycle: fromNextCycle === 'true',
          force,
        },
        toastConfig: {
          successText: 'Renewal day updated successfully',
          errorText: 'Failed to update renewal day. Please try again.',
        },
      });
      if (!res?.data) return;
    }

    if (autoCategorizationNewlyEnabled) {
      await enableVenueAutoCategorization({
        variables: { venueId: selectedVenueId },
        refetchQueries: ['VenuesForSubscriptionLimit'],
        toastConfig: {
          successText: 'Autocategorization enabled and venue migrated',
          errorText: 'Failed to enable autocategorization for this venue',
        },
      });
    }

    resetForm();
  };

  const currentPlanType =
    subscriptionDataState?.subscriptionData?.subscriptionPlanType;
  // Test venues are exempt - never force a billing link for them.
  const isLeavingPaywall =
    currentPlanType === SubscriptionPlanType.AwaitingPayment &&
    planType !== '' &&
    !selectedVenue?.isTest;

  const handleConfirm = async () => {
    // Enabling autocategorization starts the venue migration flow: require an
    // explicit extra confirmation before running anything.
    if (autoCategorizationNewlyEnabled) {
      setConfirmOpen(false);
      setAutoCategorizationWarningOpen(true);
      return;
    }
    // Leaving the paywall requires pairing a billing account first (TECH-1609):
    // gate the update behind the billing-link modal, then run it once linked.
    if (isLeavingPaywall) {
      setConfirmOpen(false);
      setBillingThenUpdate(true);
      setBillingModalOpen(true);
      return;
    }
    await runSubscriptionUpdate();
  };

  // After the destructive autocategorization migration is confirmed, still honor the
  // paywall billing gate before running the update.
  const handleAutoCategorizationConfirmed = async () => {
    setAutoCategorizationWarningOpen(false);
    if (isLeavingPaywall) {
      setBillingThenUpdate(true);
      setBillingModalOpen(true);
      return;
    }
    await runSubscriptionUpdate();
  };

  const handleBillingLinked = async () => {
    if (billingThenUpdate) {
      await runSubscriptionUpdate();
    } else if (selectedVenueId) {
      loadData({ variables: { venueId: selectedVenueId } });
    }
    setBillingThenUpdate(false);
  };

  // Disable duration radio buttons when *only* renewal day is being changed
  const durationDisabled =
    newRenewalDay !== '' && newLimit === '' && newCostRadarLimit === '';

  return (
    <>
      <Box width="100%" p={3} display="flex" justifyContent="center">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 2,
            maxWidth: '1200px',
            width: '100%',
          }}
        >
          <Card
            sx={{
              p: 2,
              backgroundColor: '#FAFBFF',
              minWidth: '600px',
            }}
          >
            <Autocomplete<VenueOption>
              fullWidth
              value={selectedVenue}
              options={venues}
              getOptionLabel={(o) => o.name}
              loading={venuesLoading}
              onChange={handleVenueChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Venue"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: venuesLoading ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              )}
            />

            {selectedVenueId && (
              <>
                {subscriptionDataState?.subscriptionData && (
                  <Box mt={2} p={2} bgcolor="#F3F6FF" borderRadius={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Venue details
                    </Typography>

                    <Typography variant="body2">
                      <strong>Current invoice limit:</strong>{' '}
                      {
                        subscriptionDataState.subscriptionData
                          .invoiceRecognitionsPerMonth
                      }
                    </Typography>
                    <Typography variant="body2">
                      <strong>Current cost radar limit:</strong>{' '}
                      {subscriptionDataState.subscriptionData
                        .orderItemsPerMonth || 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Plan:</strong>{' '}
                      {
                        subscriptionDataState.subscriptionData
                          .subscriptionPlanType
                      }
                    </Typography>
                    <Typography variant="body2">
                      <strong>Pricing version:</strong>{' '}
                      {
                        subscriptionDataState.subscriptionData
                          .pricingModelVersion
                      }
                    </Typography>

                    {renewalDay && (
                      <Typography variant="body2" color="info.main">
                        <strong>Renewal day:</strong> {renewalDay}
                      </Typography>
                    )}

                    {isV1 && (
                      <Typography variant="body2" color="error.main">
                        Please migrate this venue to pricing version V2 by
                        selecting the appropriate tier.
                      </Typography>
                    )}
                  </Box>
                )}

                {futureSubs.length > 0 && (
                  <Box mt={2} p={2} bgcolor="#FFF3E0" borderRadius={1}>
                    <Typography
                      variant="subtitle2"
                      color="warning.main"
                      gutterBottom
                    >
                      Future subscription modifications detected:
                    </Typography>
                    {futureSubs.map((f) => (
                      <Typography key={f.id} variant="body2">
                        {DateTime.fromMillis(f.start).toISODate()} – invoice
                        limit {f.invoiceRecognitionsPerMonth} – cost radar limit{' '}
                        {f.orderItemsPerMonth || 0} – plan{' '}
                        {f.subscriptionPlanType}
                      </Typography>
                    ))}
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="New Invoice Limit"
                  type="number"
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  sx={{ mt: 2 }}
                />

                <TextField
                  fullWidth
                  label="New Cost Radar Limit"
                  type="number"
                  value={newCostRadarLimit}
                  onChange={(e) => setNewCostRadarLimit(e.target.value)}
                  sx={{ mt: 2 }}
                />

                <Box mt={4}>
                  <Typography variant="subtitle1">
                    Plan change (optional)
                  </Typography>
                  <Tooltip
                    title={planTypeTooltip}
                    arrow
                    disableHoverListener={!planTypeDisabled}
                  >
                    <span>
                      <TextField
                        select
                        fullWidth
                        required={isV1}
                        error={isV1 && !planType}
                        helperText={
                          isV1
                            ? 'Required: venue is on pricing version V1'
                            : 'Leave empty to keep current plan'
                        }
                        label="Plan Type"
                        value={planType}
                        onChange={(e) => {
                          setPlanType(e.target.value as SubscriptionPlanType);
                          if (e.target.value !== '') setNewRenewalDay('');
                        }}
                        sx={{ mt: 1 }}
                        disabled={planTypeDisabled}
                      >
                        <MenuItem key="keep-current" value="">
                          Keep current
                        </MenuItem>
                        {[
                          SubscriptionPlanType.Tier_1,
                          SubscriptionPlanType.Tier_2,
                          SubscriptionPlanType.Tier_3,
                        ].map((t) => (
                          <MenuItem key={`plan-${t}`} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </TextField>
                    </span>
                  </Tooltip>
                </Box>

                <Box mt={4}>
                  <Typography variant="subtitle1">
                    Renewal day (optional)
                  </Typography>
                  <Tooltip
                    title={renewalDayTooltip}
                    arrow
                    disableHoverListener={!renewalDayDisabled}
                  >
                    <span>
                      <TextField
                        fullWidth
                        label="New Renewal Day"
                        type="number"
                        value={newRenewalDay}
                        onChange={(e) => {
                          setNewRenewalDay(e.target.value);
                          if (e.target.value !== '') setPlanType('');
                        }}
                        inputProps={{ min: 1, max: 31 }}
                        error={renewalDayError}
                        helperText={
                          renewalDayError ? 'Enter a day between 1 and 31' : ' '
                        }
                        sx={{ mt: 1 }}
                        disabled={renewalDayDisabled}
                      />
                    </span>
                  </Tooltip>
                </Box>

                <RadioGroup
                  row
                  value={fromNextCycle}
                  onChange={(e) =>
                    setFromNextCycle(e.target.value as 'false' | 'true')
                  }
                  sx={{ mt: 2 }}
                >
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="Apply Now"
                  />
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="From Next Cycle"
                  />
                </RadioGroup>

                <RadioGroup
                  row
                  value={oneTime}
                  onChange={(e) =>
                    setOneTime(e.target.value as 'false' | 'true')
                  }
                  sx={{ mt: 2 }}
                >
                  {[
                    { value: 'true', label: 'One Time' },
                    { value: 'false', label: 'Permanent' },
                  ].map(({ value, label }) => (
                    <Tooltip
                      key={value}
                      // eslint-disable-next-line max-len
                      title="This setting won't apply because renewal-day changes are always permanent. Need finer control? Contact support."
                      arrow
                      disableHoverListener={!durationDisabled}
                    >
                      <span>
                        <FormControlLabel
                          value={value}
                          control={<Radio />}
                          label={label}
                          disabled={durationDisabled}
                        />
                      </span>
                    </Tooltip>
                  ))}
                </RadioGroup>

                <Box mt={3} display="flex" flexDirection="column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableAutoCategorization}
                        disabled={autoCategorizationDisabled}
                        onChange={(e) =>
                          setEnableAutoCategorization(e.target.checked)
                        }
                      />
                    }
                    label="Enable autocategorization"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  {autoCategorizationDisabled &&
                    autoCategorizationDisabledReason && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 4 }}
                      >
                        {autoCategorizationDisabledReason}
                      </Typography>
                    )}
                  {autoCategorizationNewlyEnabled && (
                    <Typography
                      variant="body2"
                      color="warning.main"
                      sx={{ display: 'block', pl: 4 }}
                    >
                      Submitting will enable autocategorization, pre-create the
                      new category hierarchy, and mark existing supply and
                      overhead categories for migration in the venue app.
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  disabled={
                    !selectedVenueId ||
                    (!newLimit &&
                      !newCostRadarLimit &&
                      !newRenewalDay &&
                      !autoCategorizationNewlyEnabled) ||
                    (isV1 &&
                      !planType &&
                      (newLimit !== '' || newCostRadarLimit !== '')) ||
                    renewalDayError ||
                    (planType !== '' && newRenewalDay !== '')
                  }
                  onClick={openConfirmDialog}
                >
                  Update
                </Button>

                <Button
                  variant="outlined"
                  sx={{ mt: 2, ml: 2 }}
                  disabled={!selectedVenueId}
                  onClick={() => {
                    setBillingThenUpdate(false);
                    setBillingModalOpen(true);
                  }}
                >
                  Link / fix billing account
                </Button>
              </>
            )}
          </Card>

          <Card sx={{ p: 2, backgroundColor: '#FAFBFF', minWidth: 760 }}>
            <Typography variant="h3" sx={{ mt: 3, mb: 10 }}>
              ! There will be a confirmation dialog before the update !
            </Typography>
            <Typography variant="h6" gutterBottom>
              How to update a subscription
            </Typography>
            {/* ------------------------------------------------------------ */}
            {/* BASIC WORKFLOWS                                              */}
            {/* ------------------------------------------------------------ */}
            <Box component="ol" sx={{ pl: 2 }}>
              <li>
                <Typography variant="body2">
                  <strong>Change invoice&nbsp;limit (permanent):</strong>
                  &nbsp;enter a new limit and leave <em>Plan</em> and{' '}
                  <em>Renewal&nbsp;Day</em> empty. Choose&nbsp;
                  <em>Permanent</em>.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>
                    Temporary invoice&nbsp;limit (one&nbsp;cycle):
                  </strong>
                  &nbsp; enter a new limit, select&nbsp;<em>One&nbsp;Time</em>.
                  Leave other fields empty.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Change tier only:</strong>&nbsp;pick a new plan type
                  and leave the other fields empty.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Change renewal&nbsp;day:</strong>&nbsp;enter a day
                  (1-31). The change is always <em>permanent</em>; duration
                  options are disabled.
                </Typography>
              </li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Renewal-day changes cannot be combined with limit or tier updates.
              Submit them in separate requests.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              To migrate from pricing&nbsp;V1 to V2:&nbsp;first set the desired
              tier and limit with the <em>Permanent</em> option. After migration
              you can apply one-time overrides.
            </Typography>
            {/* ------------------------------------------------------------ */}
            {/* FORCE PARAMETER                                              */}
            {/* ------------------------------------------------------------ */}
            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              Using <code>force</code>
            </Typography>
            <Typography variant="body2" gutterBottom>
              If future rows already exist (for example you scheduled a change
              for next month), the system blocks conflicting updates. Re-submit
              the request with <code>force&nbsp;=&nbsp;true</code> to{' '}
              <strong>delete</strong> all pending future rows and apply the new
              values.
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <Typography variant="body2">
                  <em>Safe redo:</em> made a typo? Run the same request with{' '}
                  <code>force</code>. The timeline is rebuilt with the corrected
                  data.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <em>Idempotent:</em> multiple force calls leave only one clean
                  set of future rows.
                </Typography>
              </li>
            </Box>
            {/* ------------------------------------------------------------ */}
            {/* EDGE-CASES                                                   */}
            {/* ------------------------------------------------------------ */}
            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              Edge-cases
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <Typography variant="body2">
                  Renewal day greater than month length (for example 31 → Feb)
                  is clamped to the last day of the month.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Selecting both <em>One&nbsp;Time</em> and{' '}
                  <em>Renewal&nbsp;Day</em> converts the whole request to
                  permanent.
                </Typography>
              </li>
            </Box>
            ---
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              All updates are logged in slack channel #invoice-limit-updates
            </Typography>
          </Card>
        </Box>
      </Box>

      {/* -------------- confirm dialog -------------- */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent dividers>
          {selectedVenue && (
            <Typography gutterBottom>
              <strong>Venue:</strong> {selectedVenue.name}
            </Typography>
          )}
          <Typography gutterBottom>
            <strong>New Invoice Limit:</strong> {newLimit || 'Keep current'}
          </Typography>
          <Typography gutterBottom>
            <strong>New Cost Radar Limit:</strong>{' '}
            {newCostRadarLimit || 'Keep current'}
          </Typography>
          <Typography gutterBottom>
            <strong>New Renewal Day:</strong> {newRenewalDay || 'Keep current'}
          </Typography>
          <Typography gutterBottom>
            <strong>Plan Type:</strong> {planType || 'Keep current'}
          </Typography>
          <Typography gutterBottom>
            <strong>Apply From:</strong>{' '}
            {fromNextCycle === 'true' ? 'Next Cycle' : 'Now'}
          </Typography>
          <Typography gutterBottom>
            <strong>Duration:</strong>{' '}
            {oneTime === 'true' ? 'One Time' : 'Permanent'}
          </Typography>
          {autoCategorizationNewlyEnabled && (
            <Typography gutterBottom color="warning.main">
              <strong>Enable autocategorization:</strong> Yes (venue category
              migration flow will start)
            </Typography>
          )}

          {futureSubs.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                />
              }
              label="Force rewrite future changes"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={futureSubs.length > 0 && !force}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <BillingLinkModal
        open={billingModalOpen}
        venueId={selectedVenueId}
        onClose={() => setBillingModalOpen(false)}
        onLinked={handleBillingLinked}
      />

      <Dialog
        open={autoCategorizationWarningOpen}
        onClose={() => setAutoCategorizationWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirm autocategorization migration
        </DialogTitle>
        <DialogContent dividers>
          <Typography color="error.main" sx={{ fontWeight: 700, mb: 2 }}>
            This will enable autocategorization for this venue, pre-create the
            new category hierarchy, and mark existing supply and overhead
            categories for migration. Users in the venue will need to map their
            legacy categories before automatic categorization becomes active.
            Are you sure you want to proceed?
          </Typography>
          {selectedVenue && (
            <Typography variant="body2">
              Venue: <strong>{selectedVenue.name}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoCategorizationWarningOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleAutoCategorizationConfirmed()}
            color="error"
            variant="contained"
          >
            Yes, enable and migrate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
